import React, { useState } from "react";
import {
  Calendar,
  Loader2,
  AlertCircle,
  Users,
  FileText,
  Vote,
  CheckCircle,
  Award,
  HardHat,
  Lock,
  Edit3,
  Shield,
} from "lucide-react";
import {
  calcularDiasRestantes,
  formatarData,
  getStatusColor,
} from "../utils/dateUtils";
import { useCronograma } from "../hooks/useCronograma";
import { useAuth } from "../context/AuthContext";
import { useMandatoAtivo } from "../hooks/useMandatoAtivo";
import {
  getComissaoVisualInfo,
  isMiningCompany,
  getTipoComissao,
} from "../utils/cipaminUtils";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../components/ui/alert-dialog";

// Mapeamento de ícones por nome
const iconMap: Record<string, React.ComponentType<{ className?: string }>> = {
  Calendar,
  Users,
  FileText,
  Vote,
  CheckCircle,
  Award,
  HardHat,
};

const CronogramaTab: React.FC = () => {
  const {
    fases,
    loading,
    error,
    toggleAcao,
    carregarFases,
    // FASE 2 - Funções de confirmação e retificação (NR-05 compliance)
    confirmarAcao,
    retificarAcao,
    verificarPodeEditarAcao,
  } = useCronograma();
  const { empresaSelecionada } = useAuth();

  // IMPORTANTE: Usar o mandato ativo da tabela cp_cipa_mandato, não campos legados
  const {
    dataTerminoFormatada,
    temMandatoAtivo,
    loading: loadingMandato,
  } = useMandatoAtivo();

  // Detectar tipo de comissão (CIPA ou CIPAMIN)
  // PRIORIDADE: usar campo tipo_comissao do banco
  const tipoComissaoBanco = (empresaSelecionada as any)?.tipo_comissao;
  const cnaeEmpresa = (empresaSelecionada as any)?.cnae_principal;

  console.log("🔍 [CronogramaTab] Dados da empresa:", {
    nome: (empresaSelecionada as any)?.nome,
    tipo_comissao: tipoComissaoBanco,
    cnae_principal: cnaeEmpresa,
    empresaCompleta: empresaSelecionada,
  });

  // Usar tipo_comissao do banco se existir, senão detectar pelo CNAE ou nome da empresa
  const nomeEmpresa = (empresaSelecionada as any)?.nome;
  const isCIPAMIN =
    tipoComissaoBanco === "CIPAMIN" ||
    (!tipoComissaoBanco && isMiningCompany(cnaeEmpresa, nomeEmpresa));

  console.log("🔍 [CronogramaTab] Detecção final:", {
    tipoComissaoBanco,
    cnaeEmpresa,
    nomeEmpresa,
    isMiningCompany: isMiningCompany(cnaeEmpresa, nomeEmpresa),
    isCIPAMIN,
    tipoCalculado: getTipoComissao(cnaeEmpresa, nomeEmpresa),
  });

  const comissaoInfo = {
    tipo: isCIPAMIN ? "CIPAMIN" : "CIPA",
    norma: isCIPAMIN ? "NR-22" : "NR-05",
  };

  // FASE 2 - Estados para confirmação e retificação
  const [confirmarDialogOpen, setConfirmarDialogOpen] = useState(false);
  const [retificarDialogOpen, setRetificarDialogOpen] = useState(false);
  const [acaoSelecionada, setAcaoSelecionada] = useState<{
    faseId: string;
    acaoIndex: number;
  } | null>(null);
  const [motivoRetificacao, setMotivoRetificacao] = useState("");
  const [novaDescricao, setNovaDescricao] = useState("");
  const [loadingConfirmar, setLoadingConfirmar] = useState(false);
  const [loadingRetificar, setLoadingRetificar] = useState(false);

  // FASE 2 - Handlers para confirmação e retificação
  const handleAbrirConfirmarDialog = (faseId: string, acaoIndex: number) => {
    setAcaoSelecionada({ faseId, acaoIndex });
    setConfirmarDialogOpen(true);
  };

  const handleAbrirRetificarDialog = (
    faseId: string,
    acaoIndex: number,
    descricaoAtual: string,
  ) => {
    setAcaoSelecionada({ faseId, acaoIndex });
    setNovaDescricao(descricaoAtual);
    setMotivoRetificacao("");
    setRetificarDialogOpen(true);
  };

  const handleConfirmarAcao = async () => {
    if (!acaoSelecionada) return;

    setLoadingConfirmar(true);
    try {
      const sucesso = await confirmarAcao(
        acaoSelecionada.faseId,
        acaoSelecionada.acaoIndex,
      );
      if (sucesso) {
        setConfirmarDialogOpen(false);
        setAcaoSelecionada(null);
      }
    } finally {
      setLoadingConfirmar(false);
    }
  };

  const handleRetificarAcao = async () => {
    if (!acaoSelecionada || !motivoRetificacao.trim()) return;

    setLoadingRetificar(true);
    try {
      const sucesso = await retificarAcao(
        acaoSelecionada.faseId,
        acaoSelecionada.acaoIndex,
        { descricao: novaDescricao },
        motivoRetificacao,
      );
      if (sucesso) {
        setRetificarDialogOpen(false);
        setAcaoSelecionada(null);
        setMotivoRetificacao("");
        setNovaDescricao("");
      }
    } finally {
      setLoadingRetificar(false);
    }
  };

  console.log("🔍 Debug Cronograma:", {
    empresaNome: (empresaSelecionada as any)?.nome,
    tipoComissaoBanco,
    cnaeEmpresa,
    isCIPAMIN,
    comissaoInfo,
  });

  const isFaseConcluida = (fase: any) => {
    if (!fase.acoes_banco || fase.acoes_banco.length === 0) return false;
    return fase.acoes_banco.every((acao: any) => acao.concluida);
  };

  // Mapear artigos NR-05 para NR-22 (CIPAMIN)
  const mapearArtigoParaNR22 = (artigoNR05: string): string => {
    if (!isCIPAMIN) return artigoNR05;

    const mapeamento: Record<string, string> = {
      "Art. 5.5.2 NR-05": "Art. 22.35.2 NR-22",
      "Art. 5.5.1.1 NR-05": "Art. 22.35.1.1 NR-22",
      "Art. 5.5.3(a) NR-05": "Art. 22.35.3(a) NR-22",
      "Art. 5.5.3(b) NR-05": "Art. 22.35.3(b) NR-22",
      "Art. 5.5.3(e) NR-05": "Art. 22.35.3(e) NR-22",
      "Art. 5.5.3(f) NR-05": "Art. 22.35.3(f) NR-22",
      "Art. 5.5.3(i) NR-05": "Art. 22.35.3(i) NR-22",
      "Art. 5.7.1 NR-05": "Art. 22.36.1 NR-22",
      "Art. 5.4.7 NR-05": "Art. 22.34.7 NR-22",
    };

    return mapeamento[artigoNR05] || artigoNR05.replace("NR-05", "NR-22");
  };

  if (loading || loadingMandato) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-12 h-12 text-blue-600 animate-spin mx-auto mb-4" />
          <p className="text-gray-600">Carregando cronograma...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <AlertCircle className="w-12 h-12 text-red-600 mx-auto mb-4" />
          <p className="text-red-600 mb-4">
            Erro ao carregar cronograma: {error}
          </p>
          <button
            onClick={carregarFases}
            className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700"
          >
            Tentar Novamente
          </button>
        </div>
      </div>
    );
  }

  // LOG ADICIONAL PARA DEBUG
  console.log("========================================");
  console.log("🔍 CRONOGRAMA - DETECÇÃO DE TIPO");
  console.log("========================================");
  console.log("Empresa:", (empresaSelecionada as any)?.nome);
  console.log("CNAE:", cnaeEmpresa);
  console.log("tipo_comissao do banco:", tipoComissaoBanco);
  console.log("isCIPAMIN calculado:", isCIPAMIN);
  console.log("Norma que será usada:", comissaoInfo.norma);
  console.log("Tipo que será mostrado:", comissaoInfo.tipo);
  console.log("========================================");

  return (
    <div className="space-y-6">
      <div
        className={`rounded-lg shadow-lg p-4 md:p-6 ${
          isCIPAMIN ? "bg-orange-50" : "bg-white"
        }`}
      >
        <div className="mb-6 space-y-4">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3 md:gap-4">
            <h2
              className={`text-lg md:text-2xl font-bold flex items-center gap-2 md:gap-3 ${
                isCIPAMIN ? "text-orange-800" : "text-gray-800"
              }`}
            >
              {isCIPAMIN ? (
                <HardHat className="w-5 h-5 md:w-6 md:h-6 text-orange-600" />
              ) : (
                <Calendar className="w-5 h-5 md:w-6 md:h-6 text-blue-600" />
              )}
              <span className="leading-tight">
                Cronograma {comissaoInfo.tipo} - {comissaoInfo.norma} Conforme
              </span>
            </h2>
          </div>
          <div
            className={`${
              isCIPAMIN
                ? "bg-orange-100 border-orange-300"
                : temMandatoAtivo
                  ? "bg-blue-50 border-blue-200"
                  : "bg-orange-50 border-orange-200"
            } border rounded-lg p-3`}
          >
            <p
              className={`text-xs md:text-sm ${
                isCIPAMIN ? "text-orange-800" : "text-blue-800"
              }`}
            >
              <strong>
                Datas calculadas automaticamente conforme {comissaoInfo.norma}
              </strong>
            </p>
            <p
              className={`text-xs mt-1 ${
                isCIPAMIN
                  ? "text-orange-700"
                  : temMandatoAtivo
                    ? "text-blue-600"
                    : "text-orange-600"
              }`}
            >
              Baseado na data de término do mandato:{" "}
              <strong>
                {temMandatoAtivo
                  ? dataTerminoFormatada
                  : "⚠️ Nenhum mandato ativo configurado"}
              </strong>
            </p>
            <p
              className={`text-xs mt-1 ${
                isCIPAMIN ? "text-orange-600" : "text-blue-500"
              }`}
            >
              💡 Para gerenciar mandatos, acesse a aba <strong>Mandato</strong>
            </p>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {fases.map((fase) => {
            // Buscar o ícone correto baseado no nome armazenado no banco
            const iconName =
              typeof fase.icon === "string" ? fase.icon : "Calendar";
            const IconComponent = iconMap[iconName] || Calendar;
            const diasRestantes = calcularDiasRestantes(fase.data);
            const concluida = isFaseConcluida(fase);

            return (
              <div
                key={fase.id}
                className={`bg-white border-2 rounded-lg p-4 transition-all duration-300 hover:shadow-lg border-gray-200 hover:border-gray-300 ${
                  concluida ? "bg-green-50 border-green-200" : ""
                }`}
              >
                <div className="flex items-start gap-3 mb-3">
                  <div className={`${fase.cor} p-2 rounded-lg`}>
                    <IconComponent className="w-5 h-5 text-white" />
                  </div>
                  <div className="flex-1">
                    <h3 className="font-semibold text-gray-800 text-sm leading-tight">
                      {fase.titulo}
                    </h3>
                    <p
                      className={`text-xs mt-1 ${
                        isCIPAMIN ? "text-orange-600" : "text-gray-500"
                      }`}
                    >
                      {mapearArtigoParaNR22(fase.artigo)}
                    </p>
                    <p
                      className={`text-xs mt-1 font-medium ${
                        isCIPAMIN ? "text-orange-700" : "text-blue-600"
                      }`}
                    >
                      {fase.prazoNR}
                    </p>
                  </div>
                </div>

                <div className="space-y-2">
                  <div className="flex justify-between items-center">
                    <span className="text-sm font-medium text-gray-600">
                      Data:
                    </span>
                    <span
                      className={`text-sm font-bold ${getStatusColor(
                        fase.data,
                      )}`}
                    >
                      {formatarData(fase.data)}
                    </span>
                  </div>

                  {fase.dataFim && (
                    <div className="flex justify-between items-center">
                      <span className="text-sm font-medium text-gray-600">
                        Até:
                      </span>
                      <span
                        className={`text-sm font-bold ${getStatusColor(
                          fase.dataFim,
                        )}`}
                      >
                        {formatarData(fase.dataFim)}
                      </span>
                    </div>
                  )}

                  <div className="flex justify-between items-center">
                    <span className="text-sm font-medium text-gray-600">
                      Status:
                    </span>
                    <span
                      className={`text-xs px-2 py-1 rounded-full font-medium ${
                        diasRestantes < 0
                          ? "bg-red-100 text-red-800"
                          : diasRestantes <= 7
                            ? "bg-orange-100 text-orange-800"
                            : diasRestantes <= 30
                              ? "bg-yellow-100 text-yellow-800"
                              : "bg-green-100 text-green-800"
                      }`}
                    >
                      {diasRestantes < 0 ? "Vencido" : `${diasRestantes} dias`}
                    </span>
                  </div>
                </div>

                <div className="mt-4 pt-4 border-t border-gray-100">
                  <h4 className="font-medium text-gray-700 mb-2 text-sm">
                    Ações Obrigatórias:
                  </h4>
                  <div className="space-y-2">
                    {fase.acoes_banco &&
                      fase.acoes_banco.map((acao: any, index: number) => {
                        // FASE 2 - Verificar status da ação
                        const isConfirmada = !!acao.confirmed_at;
                        const isRetificada = acao.retificada;
                        const podeEditar = !isConfirmada && !isRetificada;

                        return (
                          <div
                            key={acao.id}
                            className={`flex items-start gap-2 p-2 rounded-lg ${
                              isConfirmada
                                ? "bg-green-50 border border-green-200"
                                : isRetificada
                                  ? "bg-gray-50 border border-gray-200 opacity-60"
                                  : ""
                            }`}
                          >
                            <input
                              id={`acao-${acao.id}`}
                              name={`acao-${acao.id}`}
                              type="checkbox"
                              checked={acao.concluida || false}
                              onChange={() =>
                                podeEditar && toggleAcao(fase.id, index)
                              }
                              disabled={!podeEditar}
                              className={`mt-1 w-3 h-3 border-green-300 rounded focus:ring-green-500 ${
                                podeEditar
                                  ? "cursor-pointer text-green-600"
                                  : "cursor-not-allowed text-gray-400"
                              }`}
                            />
                            <div className="flex-1 min-w-0">
                              <span
                                className={`text-xs ${
                                  acao.concluida
                                    ? "line-through text-gray-500"
                                    : "text-gray-700"
                                }`}
                              >
                                {acao.descricao}
                              </span>

                              {/* Indicadores de status - FASE 2 */}
                              <div className="flex flex-wrap items-center gap-1 mt-1">
                                {acao.concluida && acao.data_conclusao && (
                                  <span className="text-xs text-green-600">
                                    ✓{" "}
                                    {new Date(
                                      acao.data_conclusao,
                                    ).toLocaleDateString()}
                                  </span>
                                )}

                                {isConfirmada && (
                                  <span className="inline-flex items-center gap-1 text-xs bg-green-100 text-green-700 px-1.5 py-0.5 rounded-full">
                                    <Lock className="w-3 h-3" />
                                    Confirmada
                                  </span>
                                )}

                                {isRetificada && (
                                  <span className="inline-flex items-center gap-1 text-xs bg-orange-100 text-orange-700 px-1.5 py-0.5 rounded-full">
                                    Retificada
                                  </span>
                                )}

                                {acao.versao && acao.versao > 1 && (
                                  <span className="text-xs text-blue-600">
                                    v{acao.versao}
                                  </span>
                                )}
                              </div>

                              {/* Botões de ação - FASE 2 */}
                              {acao.concluida &&
                                !isConfirmada &&
                                !isRetificada && (
                                  <button
                                    onClick={() =>
                                      handleAbrirConfirmarDialog(fase.id, index)
                                    }
                                    className="mt-1 inline-flex items-center gap-1 text-xs text-green-600 hover:text-green-800 font-medium"
                                    title="Confirmar ação (torna imutável)"
                                  >
                                    <Shield className="w-3 h-3" />
                                    Confirmar
                                  </button>
                                )}

                              {isConfirmada && (
                                <button
                                  onClick={() =>
                                    handleAbrirRetificarDialog(
                                      fase.id,
                                      index,
                                      acao.descricao,
                                    )
                                  }
                                  className="mt-1 inline-flex items-center gap-1 text-xs text-blue-600 hover:text-blue-800 font-medium"
                                  title="Retificar ação (cria nova versão)"
                                >
                                  <Edit3 className="w-3 h-3" />
                                  Retificar
                                </button>
                              )}
                            </div>
                          </div>
                        );
                      })}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* FASE 2 - Diálogo de Confirmação */}
      <AlertDialog
        open={confirmarDialogOpen}
        onOpenChange={setConfirmarDialogOpen}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <Shield className="w-5 h-5 text-green-600" />
              Confirmar Ação
            </AlertDialogTitle>
            <AlertDialogDescription>
              Ao confirmar esta ação, ela se tornará <strong>imutável</strong>{" "}
              para fins de auditoria NR-05.
              <br />
              <br />
              <span className="text-amber-600">
                ⚠ Após confirmação, não será possível desmarcar ou editar.
              </span>
              <br />
              <span className="text-blue-600">
                💡 Se precisar fazer correções, use a opção "Retificar".
              </span>
              <br />
              <br />
              Deseja confirmar esta ação?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={loadingConfirmar}>
              Cancelar
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleConfirmarAcao}
              disabled={loadingConfirmar}
              className="bg-green-600 hover:bg-green-700"
            >
              {loadingConfirmar ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Confirmando...
                </>
              ) : (
                "Confirmar Ação"
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* FASE 2 - Diálogo de Retificação */}
      <AlertDialog
        open={retificarDialogOpen}
        onOpenChange={setRetificarDialogOpen}
      >
        <AlertDialogContent className="max-w-lg">
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <Edit3 className="w-5 h-5 text-blue-600" />
              Retificar Ação
            </AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="space-y-4">
                <p>
                  A ação original será marcada como "retificada" e uma nova
                  versão será criada. O histórico completo será preservado para
                  auditoria.
                </p>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Nova descrição (opcional)
                  </label>
                  <input
                    type="text"
                    value={novaDescricao}
                    onChange={(e) => setNovaDescricao(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
                    placeholder="Deixe em branco para manter a mesma"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Motivo da retificação{" "}
                    <span className="text-red-500">*</span>
                  </label>
                  <textarea
                    value={motivoRetificacao}
                    onChange={(e) => setMotivoRetificacao(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
                    rows={3}
                    placeholder="Descreva o motivo da retificação..."
                    required
                  />
                </div>
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={loadingRetificar}>
              Cancelar
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleRetificarAcao}
              disabled={loadingRetificar || !motivoRetificacao.trim()}
              className="bg-blue-600 hover:bg-blue-700"
            >
              {loadingRetificar ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Retificando...
                </>
              ) : (
                "Retificar Ação"
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default CronogramaTab;
